/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */

import React, {FC, memo, MouseEventHandler, ReactElement} from 'react';
import {makeStyles} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import {GeofencesDrawIcon} from '../../icons/GeofencesDrawIcon';
import {GeofencesPolygonIcon} from '../../icons/GeofencesPolygonIcon';
import {GeofencesRadialIcon} from '../../icons/GeofencesRadialIcon';
import {dlsAlt, dlsColor, dlsLayout} from '../../theme/theme';
import {CreateGeoFenceTypeEnum} from '../../types/types';
import {BodyTypography} from '../../typography/BodyTypography';
import {HeaderTypography} from '../../typography/HeaderTypography';
import {LabelTypography} from '../../typography/LabelTypography';

type GeofencesListProps = {
  changeSelectedGeofenceType: Function;
  handleViewAllClick?: Function;
  MapControlsBreadCrumbs?: null | ReactElement;
  isShowViewAllOption?: boolean;
  isPhone?: boolean;
  isAdminGeofence?: boolean;
  isBoundaryCreation?: boolean;
};

type GeofenceOptionsProps = {
  title: string;
  description: string;
  icon: ReactElement;
  onClick: MouseEventHandler;
  isFirstChild?: boolean;
  isPhone?: boolean;
};

type StyleProps = {
  isPhone?: boolean;
  isAdminGeofence?: boolean;
}

const useStyles = makeStyles(theme => ({
  container : {
    marginLeft : 10
  },
  defaultContainer : {
    marginTop : 5
  },
  firstChildContainer : {
    marginTop : ({isPhone}: StyleProps) => isPhone ? 12 : 34
  },
  labelTypography : {
    letterSpacing : 0,
    display       : 'block',
    lineHeight    : '15px'
  },
  title : {
    color  : dlsColor('black'),
    cursor : 'pointer'
  },
  description : {
    color  : dlsColor('grey3'),
    cursor : 'pointer'
  },
  geofenceHeader : {
    position      : 'absolute',
    width         : '115px',
    height        : '16px',
    left          : '5px',
    top           : '5px',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontWeight    : 700,
    fontSize      : '12px !important',
    lineHeight    : '16px !important',
    letterSpacing : '0.5px !important',
    color         : dlsColor('white'),
    marginLeft    : 6
  },
  createGeoFenceHeader : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center',
    position       : 'absolute',
    width          : ({isAdminGeofence}: StyleProps) => isAdminGeofence ? 325 : 285,
    height         : '26px',
    background     : dlsColor('grey10'),
    borderRadius   : '4px 4px 0px 0px'
  },
  viewAll : {
    color : dlsColor('white')
  },
  boxContainer : {
    display    : 'flex',
    alignItems : 'center'
  },
  iconBox : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : 40,
    height         : 40,
    cursor         : 'pointer',
    background     : dlsColor('paleBlue')
  },
  descriptionContainer : {
    marginLeft : 5
  }
}));

export const GeofenceOptions: FC<GeofenceOptionsProps> = ({
  title,
  description,
  icon,
  onClick,
  isFirstChild,
  isPhone
}: GeofenceOptionsProps) => {
  const classes = useStyles({isPhone});
  return (
    <div className={classnames(classes.container, {
      [classes.defaultContainer]    : !isFirstChild,
      [classes.firstChildContainer] : isFirstChild
    })} onClick={onClick}>
      <Box className={classes.boxContainer}>
        <Box className={classes.iconBox}>
          {React.cloneElement(icon, {
            color : dlsColor('aaBlue')
          })}
        </Box>
        <div className={classes.descriptionContainer}>
          <Box>
            <BodyTypography className={`${classes.title} ${classes.labelTypography}`}
              component='span'>
              {title}
            </BodyTypography>
          </Box>
          <Box>
            <LabelTypography variantType='label2'
              className={`${classes.description} ${classes.labelTypography}`}
              component='span'>
              {description}
            </LabelTypography>
          </Box>
        </div>
      </Box>
    </div>
  );
};

export default memo(GeofenceOptions);

export function GeofencesList({
  changeSelectedGeofenceType,
  handleViewAllClick,
  MapControlsBreadCrumbs,
  isPhone,
  isBoundaryCreation = false,
  isShowViewAllOption = true,
  isAdminGeofence = false
}: GeofencesListProps) {
  const classes    = useStyles({isAdminGeofence});
  const crumbTitle = translate(`maps:${isBoundaryCreation ? 'boundary.createBoundary' : 'geofences.createGeoFence'}`);
  return (
    <>
      {MapControlsBreadCrumbs && <MapControlsBreadCrumbs crumb={crumbTitle} />}
      {!isPhone && (
        <div className={classes.createGeoFenceHeader}>
          <div>
            <HeaderTypography variant='h4'
              className={classes.geofenceHeader}>
              {translate('maps:geofences.createGeoFence')}
            </HeaderTypography>
          </div>
          {isShowViewAllOption && (
            <TertiaryButton onClick={handleViewAllClick} className={classes.viewAll}>{translate('maps:geofences.viewAll')}</TertiaryButton>
          )}
        </div>
      )}
      <GeofenceOptions isFirstChild
        isPhone={isPhone}
        title={translate('maps:geofences.radial')}
        description={translate('maps:geofences.radialDesc')}
        icon={<GeofencesRadialIcon />}
        onClick={function() {
          changeSelectedGeofenceType(CreateGeoFenceTypeEnum.Radial);
        }} />
      <GeofenceOptions title={translate('maps:geofences.polygon')} description={translate('maps:geofences.polygonDesc')} icon={<GeofencesPolygonIcon />}
        onClick={function() {
          changeSelectedGeofenceType(CreateGeoFenceTypeEnum.Polygon);
        }} />
      <GeofenceOptions title={translate('maps:geofences.draw')} description={translate('maps:geofences.drawDesc')} icon={<GeofencesDrawIcon size={40} />}
        onClick={function() {
          changeSelectedGeofenceType(CreateGeoFenceTypeEnum.Draw);
        }} />
    </>
  );
}
